import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaBolt,
  FaBoxOpen,
  FaExclamationTriangle,
  FaHandPaper,
  FaTrash,
  FaUser,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { graphqlRequest } from '../../../utils/graphqlRequest';
import { useAdminListOverlay } from '../../../utils/useAdminPageSearch';
import PurchaseOrderForm from './PurchaseOrderForm';
import PurchaseOrderDetailModal from './PurchaseOrderDetailModal';
import '../styles/InventoryEquipment.css';
import '../styles/InventoryForecast.css';

const LOOKBACK_DAYS = 14;
const WEEK_COUNT = 12;
const FORECAST_QUOTE_KEY = 'sas.forecastQuote';

const FORECAST_Q = `
  query ForecastDash($allocLimit: Int) {
    findAllItem {
      id name code quantity available_quantity unit
      reorderLevel maxStockLevel perItemCost preferredSupplier
    }
    findAllResourceUsage {
      id usageDate itemId productName qtyUsed
    }
    findAllProject(limit: 80) {
      id projectId name status
      booking { fullName address }
    }
    getAdminRosterTasks { id project { id name } }
    findAllInventoryManagement(limit: $allocLimit) {
      quantityAssigned
      tool { id name }
      project { id name }
    }
    findAllPurchaseOrders {
      id poNumber orderDate expectedDelivery requestedBy status
      subtotal gst total lineCount supplierId
      supplier { id name code }
      lines { id description quantity quantityReceived unitCost lineTotal itemId }
    }
  }
`;

const CLOSED_PO_STATUSES = new Set(['Received', 'Cancelled']);

function isOpenPo(po) {
  return !CLOSED_PO_STATUSES.has(String(po?.status || ''));
}

function poStatusLabel(status) {
  const s = String(status || '');
  if (s === 'PendingApproval') return 'Pending Approval';
  if (s === 'PartiallyReceived') return 'Partially Received';
  return s || 'Open';
}

function PoAction({ po, createLabel, onCreate, onView }) {
  if (po) {
    return (
      <>
        <button
          type="button"
          className="ifor-btn ifor-btn--ghost ifor-btn--block"
          onClick={() => onView(po)}
        >
          Created PO
        </button>
        <p className="ifor-po-note">
          {poStatusLabel(po.status)}. Order again after this PO is received or
          cancelled.
        </p>
      </>
    );
  }
  return (
    <button
      type="button"
      className="ifor-btn ifor-btn--primary ifor-btn--block"
      onClick={onCreate}
    >
      {createLabel}
    </button>
  );
}

function onHandOf(item) {
  const avail = Number(item?.available_quantity);
  if (Number.isFinite(avail)) return Math.max(0, avail);
  return Math.max(0, Number(item?.quantity) || 0);
}

function unitOf(item) {
  return String(item?.unit || '').trim() || 'unit';
}

function parseArea(project) {
  const hay = `${project?.name || ''} ${project?.booking?.address || ''}`;
  const match = hay.match(/(\d[\d,]*)\s*(m2|m²|sqm|sq\.?\s*m)/i);
  if (!match) return null;
  return Number(String(match[1]).replace(/,/g, '')) || null;
}

function classifyProduct(name) {
  const n = String(name || '').toLowerCase();
  if (/glove|nitrile|latex/.test(n)) return 'gloves';
  if (/liner|bin bag|garbage|trash/.test(n)) return 'liners';
  if (/disinfect|sanitis|sanitiz|bleach|concentrate|cleaner/.test(n)) {
    return 'disinfectant';
  }
  return 'other';
}

function stockTone(daysLeft, onHand) {
  if (onHand <= 0) return { key: 'out', label: 'Out of Stock' };
  if (daysLeft != null && daysLeft <= 7) {
    return { key: 'critical', label: 'Critical' };
  }
  if (daysLeft != null && daysLeft <= 14) {
    return { key: 'low', label: 'Low Stock' };
  }
  return { key: 'ok', label: 'In Stock' };
}

function chartScale(values, reorderAt) {
  const raw = Math.max(reorderAt || 0, ...values, 1);
  const nice = [20, 30, 40, 50, 60, 80, 100, 120, 150, 200, 250, 300, 400, 500];
  const top = nice.find((n) => n >= raw) || Math.ceil(raw / 50) * 50;
  return { top, ticks: [0, top * 0.25, top * 0.5, top * 0.75, top] };
}

function ForecastChart({ points, reorderAt }) {
  const w = 420;
  const h = 220;
  const pad = { l: 36, r: 12, t: 14, b: 28 };
  const { top, ticks } = chartScale(
    points.map((p) => p.qty),
    reorderAt,
  );
  const innerW = w - pad.l - pad.r;
  const innerH = h - pad.t - pad.b;
  const xAt = (i) => pad.l + (i / Math.max(points.length - 1, 1)) * innerW;
  const yAt = (v) => pad.t + innerH - (v / top) * innerH;
  const path = points
    .map(
      (p, i) =>
        `${i === 0 ? 'M' : 'L'} ${xAt(i).toFixed(1)} ${yAt(p.qty).toFixed(1)}`,
    )
    .join(' ');
  const zeroIdx = points.findIndex((p) => p.qty <= 0);

  return (
    <svg
      className="ifor-chart"
      viewBox={`0 0 ${w} ${h}`}
      role="img"
      aria-label="12 week stock forecast"
    >
      {ticks.map((tick) => (
        <g key={tick}>
          <line
            x1={pad.l}
            y1={yAt(tick)}
            x2={w - pad.r}
            y2={yAt(tick)}
            stroke="#f3f4f6"
          />
          <text
            x={pad.l - 6}
            y={yAt(tick) + 3}
            textAnchor="end"
            fontSize="10"
            fill="#9ca3af"
          >
            {tick}
          </text>
        </g>
      ))}
      <line
        x1={pad.l}
        y1={pad.t}
        x2={pad.l}
        y2={h - pad.b}
        stroke="#e5e7eb"
      />
      <line
        x1={pad.l}
        y1={h - pad.b}
        x2={w - pad.r}
        y2={h - pad.b}
        stroke="#e5e7eb"
      />
      {reorderAt > 0 && (
        <line
          x1={pad.l}
          y1={yAt(reorderAt)}
          x2={w - pad.r}
          y2={yAt(reorderAt)}
          stroke="#f59e0b"
          strokeDasharray="5 4"
          strokeWidth="1.6"
        />
      )}
      <path d={path} fill="none" stroke="#6d28d9" strokeWidth="2.6" />
      {points.map((p, i) => (
        <circle
          key={p.label}
          cx={xAt(i)}
          cy={yAt(p.qty)}
          r={i === 0 ? 3.8 : 2.6}
          fill="#6d28d9"
        />
      ))}
      {zeroIdx > 0 && (
        <circle cx={xAt(zeroIdx)} cy={yAt(0)} r="4.6" fill="#dc2626" />
      )}
      {points.map((p, i) => (
        <text
          key={`lbl-${p.label}`}
          x={xAt(i)}
          y={h - 8}
          textAnchor="middle"
          fontSize="10"
          fill="#9ca3af"
        >
          {p.label}
        </text>
      ))}
    </svg>
  );
}

export default function ForecastingDashboard() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [usage, setUsage] = useState([]);
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [alloc, setAlloc] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const overlay = useAdminListOverlay(purchaseOrders);
  const selectedId = overlay.get('item') || overlay.get('product');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await graphqlRequest(FORECAST_Q, { allocLimit: 500 });
      setItems(Array.isArray(data?.findAllItem) ? data.findAllItem : []);
      setUsage(
        Array.isArray(data?.findAllResourceUsage)
          ? data.findAllResourceUsage
          : [],
      );
      setProjects(
        Array.isArray(data?.findAllProject) ? data.findAllProject : [],
      );
      setTasks(
        Array.isArray(data?.getAdminRosterTasks)
          ? data.getAdminRosterTasks
          : [],
      );
      setAlloc(
        Array.isArray(data?.findAllInventoryManagement)
          ? data.findAllInventoryManagement
          : [],
      );
      setPurchaseOrders(
        Array.isArray(data?.findAllPurchaseOrders)
          ? data.findAllPurchaseOrders
          : [],
      );
    } catch (err) {
      toast.error(err?.message || 'Failed to load forecast data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const usageByItem = useMemo(() => {
    const since = Date.now() - LOOKBACK_DAYS * 86400000;
    const map = {};
    usage.forEach((row) => {
      const key = row.itemId || String(row.productName || '').toLowerCase();
      if (!key) return;
      const t = new Date(row.usageDate).getTime();
      if (Number.isNaN(t) || t < since) return;
      map[key] = (map[key] || 0) + (Number(row.qtyUsed) || 0);
    });
    return map;
  }, [usage]);

  const forecasts = useMemo(() => {
    return items
      .map((item) => {
        const onHand = onHandOf(item);
        const used =
          usageByItem[item.id] ||
          usageByItem[String(item.name || '').toLowerCase()] ||
          0;
        const daily = used / LOOKBACK_DAYS;
        const weekly = daily * 7;
        const reorder = Number(item.reorderLevel);
        const reorderAt = Number.isFinite(reorder) && reorder > 0 ? reorder : 5;
        const daysLeft = daily > 0 ? onHand / daily : null;
        const orderQty = Math.max(
          1,
          Math.ceil(Math.max(weekly * 4 - onHand, reorderAt * 2 - onHand, 1)),
        );
        return {
          ...item,
          onHand,
          daily,
          weekly,
          daysLeft,
          reorderAt,
          orderQty,
          tone: stockTone(daysLeft, onHand),
          unit: unitOf(item),
        };
      })
      .sort((a, b) => {
        if (a.onHand <= 0 && b.onHand > 0) return -1;
        if (b.onHand <= 0 && a.onHand > 0) return 1;
        const da = a.daysLeft == null ? 9999 : a.daysLeft;
        const db = b.daysLeft == null ? 9999 : b.daysLeft;
        return da - db;
      });
  }, [items, usageByItem]);

  const alerts = useMemo(
    () =>
      forecasts.filter(
        (f) =>
          f.tone.key !== 'ok' ||
          f.onHand <= f.reorderAt ||
          (f.daysLeft != null && f.daysLeft <= 21),
      ),
    [forecasts],
  );

  useEffect(() => {
    if (selectedId) return;
    const pick =
      alerts[0] || forecasts.find((f) => f.weekly > 0) || forecasts[0];
    if (pick) overlay.patch({ item: pick.id });
  }, [alerts, forecasts, selectedId, overlay.patch]);

  const selected = forecasts.find((f) => f.id === selectedId) || null;

  const chartPoints = useMemo(() => {
    if (!selected) return [];
    let qty = selected.onHand;
    const burn = selected.weekly || 0;
    return Array.from({ length: WEEK_COUNT }, (_, i) => {
      const point = {
        label: `W${i + 1}`,
        qty: Math.max(0, Number(qty.toFixed(2))),
      };
      qty -= burn;
      return point;
    });
  }, [selected]);

  const stockoutWeek = useMemo(() => {
    const idx = chartPoints.findIndex((p) => p.qty <= 0);
    return idx >= 0 ? idx + 1 : null;
  }, [chartPoints]);

  const openPoByItem = useMemo(() => {
    const map = {};
    purchaseOrders.filter(isOpenPo).forEach((po) => {
      (po.lines || []).forEach((line) => {
        const keys = [];
        if (line.itemId) keys.push(`id:${line.itemId}`);
        const name = String(line.description || '')
          .trim()
          .toLowerCase();
        if (name) keys.push(`name:${name}`);
        keys.forEach((key) => {
          const prev = map[key];
          if (
            !prev ||
            new Date(po.orderDate || 0) > new Date(prev.orderDate || 0)
          ) {
            map[key] = po;
          }
        });
      });
    });
    return map;
  }, [purchaseOrders]);

  const existingPoFor = (row) => {
    if (!row) return null;
    return (
      openPoByItem[`id:${row.id}`] ||
      openPoByItem[`name:${String(row.name || '').trim().toLowerCase()}`] ||
      null
    );
  };

  const openPo = (row) => {
    if (!row) return;
    const existing = existingPoFor(row);
    if (existing) {
      overlay.openView(existing);
      return;
    }
    overlay.patch({ action: 'add', item: row.id }, ['view', 'edit', 'history']);
  };

  const poPrefill = useMemo(() => {
    if (!overlay.showAdd) return null;
    const row = forecasts.find((f) => f.id === selectedId);
    if (!row) return null;
    return {
      itemId: row.id,
      description: row.name,
      quantity: Math.max(1, row.orderQty || 1),
      unitCost: Number(row.perItemCost) || 0,
      status: 'Ordered',
      notes: `Forecast reorder for ${row.name}. ~${row.daily.toFixed(1)} ${row.unit}/day. Suggested ${row.orderQty} ${row.unit} for a 4-week buffer.`,
    };
  }, [overlay.showAdd, forecasts, selectedId]);

  const predictions = useMemo(() => {
    const siteStats = projects.map((p) => {
      const area = parseArea(p);
      const labourHrs =
        Math.max(1, tasks.filter((t) => t.project?.id === p.id).length) * 8;
      const cats = { disinfectant: 0, gloves: 0, liners: 0 };
      alloc
        .filter((a) => a.project?.id === p.id)
        .forEach((a) => {
          const cat = classifyProduct(a.tool?.name);
          if (cats[cat] != null) {
            cats[cat] += Number(a.quantityAssigned) || 0;
          }
        });
      return { id: p.id, name: p.name, area, labourHrs, ...cats };
    });

    const withArea = siteStats.filter((s) => s.area > 0);
    const pool = withArea.length ? withArea : siteStats;
    const totArea = pool.reduce((s, x) => s + (x.area || 500), 0) || 1;
    const per100 = {
      labour: (pool.reduce((s, x) => s + x.labourHrs, 0) / totArea) * 100,
      disinfectant:
        (pool.reduce((s, x) => s + x.disinfectant, 0) / totArea) * 100,
      gloves: (pool.reduce((s, x) => s + x.gloves, 0) / totArea) * 100,
      liners: (pool.reduce((s, x) => s + x.liners, 0) / totArea) * 100,
    };

    const fallback = [
      { name: 'New Medical Centre', area: 1000 },
      { name: 'West Suburb Office', area: 500 },
      { name: 'Retail Store Complex', area: 800 },
    ];
    const sources = siteStats.length
      ? [...siteStats]
          .sort((a, b) => (b.area || 0) - (a.area || 0))
          .slice(0, 3)
          .map((s) => ({
            name: s.name,
            area: s.area || Math.round(s.labourHrs * 60) || 500,
          }))
      : fallback;
    while (sources.length < 3) sources.push(fallback[sources.length]);

    return {
      similarCount: Math.max(pool.length, 1),
      cards: sources.slice(0, 3).map((s) => {
        const area = s.area || 500;
        const scale = area / 100;
        return {
          name: s.name,
          area,
          labour: Math.max(4, Math.round((per100.labour || 1.4) * scale)),
          disinfectant: Math.max(
            1,
            Math.round((per100.disinfectant || 0.3) * scale * 10) / 10,
          ),
          gloves: Math.max(20, Math.round((per100.gloves || 10) * scale)),
          liners: Math.max(10, Math.round((per100.liners || 4) * scale)),
        };
      }),
    };
  }, [projects, tasks, alloc]);

  const goQuote = (card) => {
    const payload = {
      title: `Cleaning quote — ${card.name}`,
      siteSize: String(card.area),
      staffRequired: String(Math.max(1, Math.round(card.labour / 38))),
      scopeOfWork: [
        `Predicted weekly resource needs for ${card.name} (${card.area.toLocaleString()}m²):`,
        `• Labour: ${card.labour} hrs/week`,
        `• Disinfectant: ${card.disinfectant} L/week`,
        `• Gloves: ${card.gloves} pairs/week`,
        `• Bin liners: ${card.liners}/week`,
        '',
        `Based on ${predictions.similarCount} similar site${
          predictions.similarCount === 1 ? '' : 's'
        } in the system.`,
      ].join('\n'),
    };
    try {
      sessionStorage.setItem(FORECAST_QUOTE_KEY, JSON.stringify(payload));
    } catch {
      /* ignore quota errors */
    }
    navigate({ pathname: '/', search: '?page=proposals&from=forecast' });
  };

  const selectedOpenPo = existingPoFor(selected);

  return (
    <div className="ifor-page">
      <h1 className="ifor-title">Forecasting &amp; Reordering</h1>

      {loading ? (
        <div className="ifor-empty">Building forecast from stock and usage…</div>
      ) : (
        <div className="ifor-grid">
          <section className="ifor-card">
            <h2 className="ifor-card-title">
              <FaExclamationTriangle className="ifor-card-ico ifor-card-ico--warn" />
              Low Stock Alerts
            </h2>
            {alerts.length === 0 ? (
              <p className="ifor-muted">No low-stock items right now.</p>
            ) : (
              <ul className="ifor-alert-list">
                {alerts.slice(0, 6).map((row) => (
                  <li
                    key={row.id}
                    className={`ifor-alert${selectedId === row.id ? ' is-active' : ''}`}
                  >
                    <button
                      type="button"
                      className="ifor-alert-main"
                      onClick={() => overlay.patch({ item: row.id })}
                    >
                      <div className="ifor-alert-top">
                        <strong>{row.name}</strong>
                        <span className={`ifor-pill ifor-pill--${row.tone.key}`}>
                          {row.tone.label}
                        </span>
                      </div>
                      <div className="ifor-alert-meta">
                        Stock: <b>{row.onHand} {row.unit}</b>
                      </div>
                      <div className="ifor-alert-meta">
                        Daily usage: ~{row.daily.toFixed(1)}
                      </div>
                      <div
                        className={`ifor-alert-days${
                          row.tone.key === 'out' || row.tone.key === 'critical'
                            ? ' is-danger'
                            : row.tone.key === 'ok'
                              ? ''
                              : ' is-warn'
                        }`}
                      >
                        {row.onHand <= 0
                          ? 'Out of stock'
                          : row.daysLeft == null
                            ? 'No recent usage to project'
                            : `~${Math.max(0, Math.round(row.daysLeft))} days remaining`}
                      </div>
                    </button>
                    <PoAction
                      po={existingPoFor(row)}
                      createLabel="Create PO"
                      onCreate={() => openPo(row)}
                      onView={overlay.openView}
                    />
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="ifor-card">
            <div className="ifor-card-head">
              <h2 className="ifor-card-title">
                Stock Forecast
                {selected ? ` — ${selected.name}` : ''}
              </h2>
              <p className="ifor-card-sub">Next 12 weeks projection.</p>
              {forecasts.length > 0 && (
                <select
                  className="ifor-select"
                  value={selectedId}
                  onChange={(e) => overlay.patch({ item: e.target.value })}
                  aria-label="Product to forecast"
                >
                  {forecasts.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.name}
                    </option>
                  ))}
                </select>
              )}
            </div>
            {!selected ? (
              <p className="ifor-muted">Add products to see a forecast.</p>
            ) : (
              <>
                <ForecastChart
                  points={chartPoints}
                  reorderAt={selected.reorderAt}
                />
                <div className="ifor-callout">
                  <FaExclamationTriangle className="ifor-callout-ico" />
                  <div>
                    <p>
                      {selected.onHand <= 0
                        ? 'Already out of stock'
                        : selected.daysLeft != null
                          ? `Projected stockout in ~${Math.max(
                              0,
                              Math.round(selected.daysLeft),
                            )} days`
                          : stockoutWeek
                            ? `Projected stockout in ~${stockoutWeek} week${
                                stockoutWeek === 1 ? '' : 's'
                              }`
                            : 'Stock lasts beyond 12 weeks'}{' '}
                      at current usage rate of ~{selected.weekly.toFixed(1)}
                      {selected.unit}/week.
                    </p>
                    <p className="ifor-callout-rec">
                      {selectedOpenPo
                        ? `${selectedOpenPo.poNumber} is already ${poStatusLabel(
                            selectedOpenPo.status,
                          ).toLowerCase()}. Stock updates when it is received.`
                        : `Recommendation: Order ${selected.orderQty} ${selected.unit} now to maintain a 4-week buffer.`}
                    </p>
                  </div>
                </div>
                <PoAction
                  po={selectedOpenPo}
                  createLabel="Create Purchase Order"
                  onCreate={() => openPo(selected)}
                  onView={overlay.openView}
                />
              </>
            )}
          </section>

          <section className="ifor-card">
            <h2 className="ifor-card-title">
              <FaBolt className="ifor-card-ico ifor-card-ico--purple" />
              AI Resource Prediction
            </h2>
            {predictions.cards[0] && (
              <>
                <div className="ifor-predict">
                  <div className="ifor-predict-title">
                    {predictions.cards[0].name} (
                    {predictions.cards[0].area.toLocaleString()}m²) predicted
                    needs:
                  </div>
                  <ul className="ifor-predict-stats">
                    <li>
                      <span className="ifor-stat-ico">
                        <FaUser />
                      </span>
                      Labour: {predictions.cards[0].labour} hrs/week
                    </li>
                    <li>
                      <span className="ifor-stat-ico">
                        <FaBoxOpen />
                      </span>
                      Disinfectant: {predictions.cards[0].disinfectant}L/week
                    </li>
                    <li>
                      <span className="ifor-stat-ico">
                        <FaHandPaper />
                      </span>
                      Gloves: {predictions.cards[0].gloves} pairs/week
                    </li>
                    <li>
                      <span className="ifor-stat-ico">
                        <FaTrash />
                      </span>
                      Bin liners: {predictions.cards[0].liners}/week
                    </li>
                  </ul>
                  <p className="ifor-predict-note">
                    Based on {predictions.similarCount} similar site
                    {predictions.similarCount === 1 ? '' : 's'} in your system
                  </p>
                </div>
                <button
                  type="button"
                  className="ifor-btn ifor-btn--primary ifor-btn--block"
                  onClick={() => goQuote(predictions.cards[0])}
                >
                  Apply to Quote →
                </button>
              </>
            )}
            <div className="ifor-more">
              <div className="ifor-more-label">More Predictions</div>
              {predictions.cards.slice(1).map((card) => (
                <button
                  key={card.name}
                  type="button"
                  className="ifor-more-item"
                  onClick={() => goQuote(card)}
                >
                  {card.name} ({card.area.toLocaleString()}m²)
                </button>
              ))}
            </div>
          </section>
        </div>
      )}

      {overlay.viewing && (
        <PurchaseOrderDetailModal
          po={overlay.viewing}
          onClose={overlay.closeOverlay}
          onEdit={overlay.openEdit}
        />
      )}

      {overlay.showForm && (
        <PurchaseOrderForm
          key={overlay.editing?.id || poPrefill?.itemId || 'new'}
          prefill={poPrefill}
          editData={overlay.editing}
          onClose={overlay.closeOverlay}
          onSaved={() => {
            overlay.closeOverlay();
            void load();
          }}
        />
      )}
    </div>
  );
}
